import { type Task, TaskGroupBy } from "@common/types/task";
import type { BoardPlacement } from "@design-system/Board/Board.types";
import {
    buildUnsavedTask,
    getPlaceholderTaskFields,
    type TaskPlaceholderFields,
    type TaskPlaceholderFilters,
} from "../taskPlaceholderFields";
import {
    ENTITY_LANE_BY,
    parseEntityLaneId,
    type TaskBoardLaneBy,
} from "./taskBoardLanes";
import type { TaskComparator } from "./taskBoardSort";

export const TASK_BOARD_DRAFT_ID = "task-board-draft";

/** The value a lane by each grouping gives a new task; the rest come from its column. */
const LANE_VALUES: Record<
    TaskGroupBy,
    (laneFields: TaskPlaceholderFields) => Partial<TaskPlaceholderFields>
> = {
    [TaskGroupBy.STATUS]: ({ status }) => ({ status }),
    [TaskGroupBy.PRIORITY]: ({ priority }) => ({ priority }),
    [TaskGroupBy.ASSIGNEE]: ({ assigneeId }) => ({ assigneeId }),
    [TaskGroupBy.TASK_GROUP]: ({ groupId }) => ({ groupId }),
    [TaskGroupBy.CLOSE_STAGE]: ({ closeStageId }) => ({ closeStageId }),
    [TaskGroupBy.CLOSE]: ({ closeId }) => ({ closeId }),
    [TaskGroupBy.DUE_DATE]: ({ dueDate }) => ({ dueDate }),
    [TaskGroupBy.CLIENT]: () => ({}),
};

interface DraftTaskFieldsArgs {
    placement: BoardPlacement;
    groupBy: TaskGroupBy;
    laneBy: TaskBoardLaneBy | null;
    /** A task in the column, whose values List's placeholder copies. */
    sampleTask: Task | undefined;
    filters: TaskPlaceholderFilters;
}

const getLaneValues = ({
    laneId,
    laneBy,
    filters,
}: {
    laneId: string;
    laneBy: TaskBoardLaneBy;
    filters: TaskPlaceholderFilters;
}): Partial<TaskPlaceholderFields> => {
    if (laneBy === ENTITY_LANE_BY) {
        const entityId = parseEntityLaneId(laneId);
        return { entityIds: entityId === null ? undefined : [entityId] };
    }
    return LANE_VALUES[laneBy](
        getPlaceholderTaskFields({
            groupKey: laneId,
            groupBy: laneBy,
            isGroupedByCloseStage: laneBy === TaskGroupBy.CLOSE_STAGE,
            sampleTask: undefined,
            filters,
        }),
    );
};

/**
 * What a task created in a board cell starts with: List's placeholder
 * values for its column, with the lane's own value on top.
 */
export const getDraftTaskFields = ({
    placement,
    groupBy,
    laneBy,
    sampleTask,
    filters,
}: DraftTaskFieldsArgs): TaskPlaceholderFields => {
    const columnFields = getPlaceholderTaskFields({
        groupKey: placement.columnId,
        groupBy,
        isGroupedByCloseStage: groupBy === TaskGroupBy.CLOSE_STAGE,
        sampleTask,
        filters,
    });
    return laneBy === null || placement.laneId === null
        ? columnFields
        : {
              ...columnFields,
              ...getLaneValues({ laneId: placement.laneId, laneBy, filters }),
          };
};

/** The cards beside the `+` that opened a draft: null past either end of the cell. */
export interface TaskDraftNeighbours {
    before: Task | null;
    after: Task | null;
}

interface CompareDraftArgs {
    neighbours: TaskDraftNeighbours;
    task: Task;
    compareTasks: TaskComparator;
}

/**
 * The draft's order against `task` in its cell: right after the card above
 * its `+`, else right before the card below, so it opens where the `+` was
 * whatever the sort. With neither (an empty cell) it goes first.
 */
export const compareDraftTo = ({
    neighbours: { before, after },
    task,
    compareTasks,
}: CompareDraftArgs) => {
    if (before !== null) {
        return task.id === before.id || compareTasks(before, task) > 0 ? 1 : -1;
    }
    if (after !== null && task.id !== after.id) {
        return compareTasks(after, task) > 0 ? 1 : -1;
    }
    return -1;
};

/** The draft card's stand-in task, until its title is in. */
export const buildDraftTask = (fields: TaskPlaceholderFields): Task =>
    buildUnsavedTask({ id: TASK_BOARD_DRAFT_ID, fields });
